import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { AppTopbarComponent } from '@clinic-platform/ui-components';

import { AuthService } from '../auth/auth.service';
import { I18nService } from '../i18n/i18n.service';
import { SystemAdminContextService } from '../context/system-admin-context.service';
import { TenantBrandingService } from '../branding/tenant-branding.service';

@Component({
  selector: 'app-shell-topbar',
  standalone: true,
  imports: [AppTopbarComponent],
  template: `
    <lib-app-topbar [title]="shellTitle">
      @if (tenantLogoUrl) {
        <img
          libAppTopbarStart
          [src]="tenantLogoUrl"
          alt="Tenant logo"
          class="topbar__brand-logo"
        />
      }
      <div class="topbar__spacer"></div>
    </lib-app-topbar>
  `,
  styles: [`
    .topbar__brand-logo {
      max-width: 132px;
      max-height: 38px;
      object-fit: contain;
      display: block;
      flex: 0 0 auto;
    }

    .topbar__spacer {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      flex: 1 1 auto;
    }

    @media (max-width: 640px) {
      .topbar__brand-logo {
        max-width: 108px;
        max-height: 32px;
      }
    }
  `]
})
export class ShellTopbarComponent implements OnInit, OnDestroy
{
  private readonly authService = inject(AuthService);
  private readonly i18n = inject(I18nService);
  private readonly tenantBranding = inject(TenantBrandingService);
  private readonly systemAdminContext = inject(SystemAdminContextService);
  private readonly subscriptions = new Subscription();
  protected tenantDisplayName = '';
  protected tenantLogoUrl: string | null = null;
  protected showCompanyNameInHeader = true;

  protected get isSystemAdmin(): boolean
  {
    return this.authService.getCurrentRole() === 'system_admin';
  }

  protected get shellTitle(): string {
    if (!this.showCompanyNameInHeader) {
      return '';
    }

    return this.tenantDisplayName || this.i18n.translate('app.shellTitle');
  }

  public ngOnInit(): void
  {
    this.bindBranding();
  }

  public ngOnDestroy(): void
  {
    this.subscriptions.unsubscribe();
  }

  private bindBranding(): void {
    this.applyBrandingForCurrentContext();

    if (!this.isSystemAdmin) {
      return;
    }

    this.subscriptions.add(
      this.systemAdminContext.companyId$.subscribe(() => {
        this.applyBrandingForCurrentContext();
      }),
    );
    this.subscriptions.add(
      this.systemAdminContext.scopeMode$.subscribe(() => {
        this.applyBrandingForCurrentContext();
      }),
    );
  }

  private applyBrandingForCurrentContext(): void {
    if (this.isSystemAdmin) {
      if (this.systemAdminContext.isGlobalScope()) {
        this.tenantDisplayName = '';
        this.tenantLogoUrl = null;
        this.showCompanyNameInHeader = true;
        return;
      }

      const companyId = this.systemAdminContext.getCompanyId();
      if (!companyId?.trim()) {
        this.tenantDisplayName = '';
        this.tenantLogoUrl = null;
        this.showCompanyNameInHeader = true;
        return;
      }

      this.subscriptions.add(
        this.tenantBranding.getBrandingByCompanyId(companyId).subscribe((branding) => {
          this.tenantDisplayName = branding?.displayName ?? '';
          this.tenantLogoUrl = branding?.logoUrl ?? null;
          this.showCompanyNameInHeader = branding?.showCompanyNameInHeader ?? true;
        }),
      );
      return;
    }

    const preferredSlug = this.authService.getPreferredCompanySlug();
    if (!preferredSlug) {
      this.tenantDisplayName = '';
      this.tenantLogoUrl = null;
      this.showCompanyNameInHeader = true;
      return;
    }

    this.subscriptions.add(
      this.tenantBranding.getBranding(preferredSlug).subscribe((branding) => {
        this.tenantDisplayName = branding?.displayName ?? '';
        this.tenantLogoUrl = branding?.logoUrl ?? null;
        this.showCompanyNameInHeader = branding?.showCompanyNameInHeader ?? true;
      }),
    );
  }

}
